/**
 * Database Property Types for FreshCards
 * Supports all property types like Notion/AppFlowy
 */

// Core property types
export type PropertyType = 
  | 'title'        // Primary title property
  | 'text'         // Rich text content
  | 'number'       // Numeric values with formatting
  | 'select'       // Single selection from options
  | 'multi_select' // Multiple selections from options
  | 'date'         // Date with optional time
  | 'checkbox'     // Boolean toggle
  | 'url'          // URL/link
  | 'files'        // File attachments/cover images
  | 'relation'     // Linked items from another database
  | 'formula'      // Calculated values
  | 'status'       // Status workflow (like Notion)
  | 'person'       // User/person reference
  | 'phone'        // Phone number
  | 'email'        // Email address
  | 'created_time' // Auto: creation timestamp
  | 'last_edited_time'; // Auto: last edit timestamp

// Status option for status property
export interface StatusOption {
  id: string;
  name: string;
  color: string; // 'gray' | 'blue' | 'green' | 'yellow' | 'orange' | 'red' | 'purple' | 'pink'
  icon?: string;
}

// Select/Multi-select option
export interface SelectOption {
  id: string;
  name: string;
  color: string;
}

// Number formatting
export type NumberFormat = 
  | 'number'        // Plain number
  | 'dollar'        // $1,234.56
  | 'euro'          // €1.234,56
  | 'yen'           // ¥1,234
  | 'pound'         // £1,234.56
  | 'percent'       // 12.34%
  | 'comma'         // 1,234,567
  | 'decimal';      // 1234.56

// Property visibility on card
export interface PropertyDisplayConfig {
  /** Show this property on the card front */
  visible: boolean;
  /** Position/order on card */
  position: number;
  /** Display as badge or inline */
  displayStyle: 'badge' | 'inline' | 'icon';
  /** Only show if property has value */
  hideIfEmpty: boolean;
  /** Compact mode for multiple properties */
  compact: boolean;
}

// Property definition for database schema
export interface PropertyDefinition {
  id: string;
  name: string;
  type: PropertyType;
  
  // Display configuration
  visible: boolean;
  position: number;
  
  // Type-specific options
  options?: SelectOption[];           // For select, multi_select
  statusOptions?: StatusOption[];      // For status
  numberFormat?: NumberFormat;         // For number
  prefix?: string;                     // For number (e.g., "$")
  suffix?: string;                     // For number (e.g., "%")
  dateFormat?: string;                 // For date
  
  // Relation config
  relatedDatabaseId?: string;
  relationType?: 'single' | 'multiple';
  
  // Formula expression
  formula?: string;
  
  // Read-only properties (auto-generated)
  readOnly?: boolean;
  
  // Default value
  defaultValue?: unknown;
}

// Property value - runtime value for a property
export interface PropertyValue {
  propertyId: string;
  value: unknown; // Type depends on property type
}

// Database property CSS colors (Notion-inspired)
export const PROPERTY_COLORS = {
  gray: { bg: '#E8E8E8', text: '#4D4D4D' },
  blue: { bg: '#DDEEFF', text: '#0066CC' },
  green: { bg: '#DDFFDD', text: '#008800' },
  yellow: { bg: '#FFFFDD', text: '#999900' },
  orange: { bg: '#FFEEDD', text: '#CC6600' },
  red: { bg: '#FFDDDD', text: '#CC0000' },
  purple: { bg: '#EEDDFF', text: '#8800CC' },
  pink: { bg: '#FFDDFF', text: '#CC0088' },
  brown: { bg: '#EEDDCC', text: '#885500' },
} as const;

export type PropertyColor = keyof typeof PROPERTY_COLORS;
